import { useMsal } from '@azure/msal-react'
import { useCallback } from 'react'
import { useIntl } from 'react-intl'
import { useAppSelector } from '../../app/hooks'
import { selectIsAnyDirty } from '../../app/store/slices/dirtyStateSlice'
import { useOptionalConfirmDialog } from '../../components/ConfirmDialog'
import { useEndActiveImpersonationSessionMutation } from '../../app/redux-fetch/apiImpersonation'
import {
  clearImpersonationSession,
  clearPendingStart,
  getImpersonationSessionId,
} from '../CommonSettings/Impersonation/impersonationSessionStorage'
import { clearActivePersona } from './personaStorage'
import signOutMessages from './signOutMessages'

// E8 F8-6 (story 8.6.1): the sign-out coordinator. Clears the persona/context the SPA persisted, then
// hands off to Entra's RP-initiated logout, which returns to /signed-out (postLogoutRedirectUri set in
// authConfig).
//
// E11 impersonation teardown, in order:
//  - R4 (WI 617) SERVER: if a session is active, POST /sessions/active/end FIRST so the audit trail records
//    a clean SignedOut (EndReason 4) end rather than leaving it for the daily auto-end timer. Best-effort —
//    a failure never blocks logout (the timer + the JWT-revocation hook are the backstop), and it is skipped
//    entirely when not impersonating (no stored session id) so the common case is unchanged.
//  - R3 LOCAL: clear the impersonation client-state (session id + pending start) so a re-sign-in on the same
//    tab (sessionStorage survives a logout redirect) sends no stale X-LogikSpark-Impersonation-Session-Id.
//
// E8 8.6.2 / WI 579: if any page registered unsaved changes (useDirtyState), confirm BEFORE any teardown
// step so the user can't lose work. The optional confirm accessor is null outside the app shell
// (PersonaGate) — harmless there, since a splash screen never has dirty state.
export function useSignOut() {
  const { instance } = useMsal()
  const { formatMessage } = useIntl()
  const confirmDialog = useOptionalConfirmDialog()
  const isAnyDirty = useAppSelector(selectIsAnyDirty)
  const [endActiveImpersonation] = useEndActiveImpersonationSessionMutation()

  return useCallback(async () => {
    if (isAnyDirty && confirmDialog) {
      const proceed = await confirmDialog.confirm({
        title: formatMessage(signOutMessages.dirtyTitle),
        message: formatMessage(signOutMessages.dirtyMessage),
        confirmText: formatMessage(signOutMessages.dirtyConfirm),
        cancelText: formatMessage(signOutMessages.dirtyCancel),
      })
      if (!proceed) {
        return
      }
    }

    // R4: end the active impersonation session server-side before logout (best-effort). Only when a session
    // id is stored — this request still carries the impersonation header (cleared just below), which the
    // server ignores for active/end (it resolves the caller's own session), but sending it is harmless. A
    // failure is swallowed so it can never strand the user mid-logout.
    if (getImpersonationSessionId() != null) {
      try {
        await endActiveImpersonation().unwrap()
      } catch {
        // best-effort — the daily auto-end timer + the JWT-revocation hook bound the session lifetime.
      }
    }

    // Drop the active persona (sent as X-LogikSpark-Persona-Id; written by the persona splash, F8-4), the
    // legacy selected-context key, and the impersonation client-state (session id + any pending start), so a
    // later sign-in on the same browser starts clean and sends no stale impersonation header.
    clearActivePersona()
    clearImpersonationSession()
    clearPendingStart()
    localStorage.removeItem('selectedCustomerTenant')

    void instance.logoutRedirect()
  }, [instance, confirmDialog, isAnyDirty, formatMessage, endActiveImpersonation])
}
